import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Tablet } from './CustomIcons';
import { useAuth } from '../context/AuthContext';

/* Canonical role keys used by the prototype (/prototype). */
export const PROTO_TO_WEB_ROLE = {
  learner: 'learner',
  case_manager: 'case_manager',
  instructor: 'instructor',
  facility_admin: 'facility_admin',
  employer: 'employer_partner',
  training_provider: 'provider_partner',
  youth_learner: 'youth_learner',
  admin: 'facility_admin', // TTI Admin has no dashboard equivalent; fall back to facility admin
};

export const WEB_TO_PROTO_ROLE = {
  learner: 'learner',
  case_manager: 'case_manager',
  instructor: 'instructor',
  facility_admin: 'facility_admin',
  employer_partner: 'employer',
  provider_partner: 'training_provider',
  youth_learner: 'youth_learner',
};

const ACTIVE_ROLE_KEY = 'assistivos_active_role';
const PENDING_SWITCH_KEY = 'assistivos_pending_role_switch';

/**
 * ViewToggle — pill button that flips between /prototype and /dashboard.
 * Preserves the active role via localStorage (key: `assistivos_active_role`).
 *
 * Props:
 *  - currentRole: canonical role key (as used on /prototype) or a web role key.
 *  - source: 'prototype' | 'dashboard' (which view is currently rendering).
 */
export default function ViewToggle({ currentRole, source }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { hydrateIfNeeded } = useAuth();
  const inferredSource = source || (location.pathname.startsWith('/prototype') ? 'prototype' : 'dashboard');
  const isPrototype = inferredSource === 'prototype';
  const label = isPrototype ? 'Website View' : 'Prototype';
  const Icon = isPrototype ? LayoutDashboard : Tablet;
  const target = isPrototype ? '/dashboard' : '/prototype';
  const testId = isPrototype ? 'view-toggle-to-website' : 'view-toggle-to-prototype';
  const [hover, setHover] = React.useState(false);
  const [focus, setFocus] = React.useState(false);

  const persistRole = () => {
    if (!currentRole) return;
    // Store canonical (prototype) role key regardless of source.
    const canonical = isPrototype ? currentRole : (WEB_TO_PROTO_ROLE[currentRole] || currentRole);
    try {
      localStorage.setItem(ACTIVE_ROLE_KEY, canonical);
      sessionStorage.setItem(PENDING_SWITCH_KEY, '1');
    } catch (_e) { /* ignore */ }
  };

  const handleClick = async () => {
    persistRole();
    // If we're leaving /prototype for a protected route and AuthContext user is
    // null (because the initial mount skipped /auth/me on the public prototype),
    // rehydrate from the httpOnly cookie first to avoid a login redirect.
    if (isPrototype && hydrateIfNeeded) {
      try { await hydrateIfNeeded(); } catch (_e) { /* ignore */ }
    }
    navigate(target);
  };

  const active = hover || focus;
  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: '36px',
    padding: '0 12px',
    borderRadius: '8px',
    border: '0.5px solid rgba(255,255,255,0.30)',
    background: active ? '#f97316' : '#0f2744',
    color: active ? '#0f2744' : '#ffffff',
    fontFamily: 'var(--p-font-body, "Source Sans 3", "Source Sans Pro", system-ui, sans-serif)',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 150ms ease, color 150ms ease',
    whiteSpace: 'nowrap',
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
      data-testid={testId}
      aria-label={`${label} — switch views`}
      style={baseStyle}
    >
      <Icon size={16} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}

/**
 * Vertical hairline separator matching spec (1px white 20% opacity).
 * Height matches the 36px pill so it aligns cleanly with the toggle.
 */
export const ToggleSeparator = ({ tone = 'light' }) => (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-block',
      width: '1px',
      height: '24px',
      background: tone === 'light' ? 'rgba(255,255,255,0.20)' : 'rgba(15,39,68,0.20)',
      alignSelf: 'center',
    }}
  />
);

export { ACTIVE_ROLE_KEY, PENDING_SWITCH_KEY };
